import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../slices/authSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../store/store';
import { fetchActiveEntryForToday, startEntry, pauseEntry, stopEntry } from '../slices/timeEntrySlice';
import { fetchWorkSchedules } from '../slices/workScheduleSlice';

const weekdays = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

const isBeforeScheduledStart = (startTime: string | undefined, now: Date) => {
  if (!startTime) return false;

  const [hours, minutes] = startTime.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;

  const scheduledStart = new Date(now);
  scheduledStart.setHours(hours, minutes, 0, 0);

  return now < scheduledStart;
};

const isAfterScheduledEnd = (endTime: string | undefined, now: Date) => {
  if (!endTime) return false;

  const [hours, minutes] = endTime.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;

  const scheduledEnd = new Date(now);
  scheduledEnd.setHours(hours, minutes, 0, 0);

  return now > scheduledEnd;
};

const isScheduleValidForDate = (schedule: any, date: Date) => {
  const dateTime = date.getTime();
  const validFrom = schedule.validFrom ? new Date(schedule.validFrom).getTime() : null;
  const validTo = schedule.validTo ? new Date(schedule.validTo).getTime() : null;

  return validFrom !== null && validFrom <= dateTime && (validTo === null || validTo >= dateTime);
};

const TopBar = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  const user = useSelector((state: RootState) => state.auth.user);

  const entries = useSelector((state: RootState) => state.time.entries);
  const storedActiveEntry = useSelector((state: RootState) => state.time.activeEntry);
  const schedules = useSelector((state: RootState) => state.work.schedules);
  const workSchedulesLoading = useSelector((state: RootState) => state.work.loading);

  const activeEntry =
    storedActiveEntry && storedActiveEntry.userId === user?._id
      ? storedActiveEntry
      : entries.find((e) => !e.endTime && e.userId === user?._id);

  const [elapsedTime, setElapsedTime] = useState(0);
  const [pauseTime, setPauseTime] = useState(0);
  const [now, setNow] = useState(() => new Date());

  const todaySchedule =
    schedules
      .filter(
        (schedule) =>
          schedule.userId === user?._id &&
          schedule.dayOfWeek === weekdays[now.getDay()] &&
          isScheduleValidForDate(schedule, now)
      )
      .sort(
        (a, b) =>
          new Date(b.validFrom || 0).getTime() - new Date(a.validFrom || 0).getTime()
      )[0] ||
    schedules.find(
      (schedule) =>
        schedule.userId === user?._id &&
        schedule.dayOfWeek === weekdays[now.getDay()] &&
        !schedule.validFrom
    );
  const startBlockedByFixedTime =
    todaySchedule?.mode === 'fixed' &&
    (isBeforeScheduledStart(todaySchedule.startTime, now) ||
      isAfterScheduledEnd(todaySchedule.endTime, now));
  const startDisabled =
    !!activeEntry || startBlockedByFixedTime || workSchedulesLoading;

  // 🔒 Lokaler Lock (wichtig gegen doppelte Klicks)
  const isStartingRef = useRef(false);
  const isPausingRef = useRef(false);
  const isStoppingRef = useRef(false);

  useEffect(() => {
    if (!user || isLoginPage) return;
    dispatch(fetchWorkSchedules());
  }, [dispatch, user, isLoginPage]);

  useEffect(() => {
    if (!user?._id || isLoginPage) return;

    dispatch(fetchActiveEntryForToday(user._id));
  }, [dispatch, user?._id, isLoginPage, location.pathname]);

  useEffect(() => {
    if (isLoginPage) return;

    const interval = setInterval(() => {
      setNow(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, [isLoginPage]);

  // 🔁 Timer (nur für Anzeige, KEIN Re-Render der Buttons)
  useEffect(() => {
    if (!activeEntry) {
      setElapsedTime(0);
      setPauseTime(0);
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const startMs = new Date(activeEntry.startTime).getTime();
      const totalPaused = activeEntry.pausedDuration || 0;

      if (activeEntry.isPaused && activeEntry.pauseStart) {
        const pauseStartMs = new Date(activeEntry.pauseStart).getTime();
        const pauseSeconds = Math.floor((now - pauseStartMs) / 1000);
        setPauseTime(pauseSeconds);
      } else {
        let totalElapsed =
          Math.floor((now - startMs) / 1000) - totalPaused;
        if (totalElapsed < 0) totalElapsed = 0;
        setElapsedTime(totalElapsed);
        setPauseTime(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [activeEntry]);

  const formatTime = (seconds: number) => {
    seconds = Math.floor(seconds);
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // ✅ START (ohne doppelte Klicks)
  const handleStart = async () => {
    if (startDisabled || isStartingRef.current) return;

    isStartingRef.current = true;

    try {
      await dispatch(startEntry()).unwrap();
    } finally {
      isStartingRef.current = false;
    }
  };

  // ✅ PAUSE
  const handlePause = async () => {
    if (!activeEntry || isPausingRef.current) return;

    isPausingRef.current = true;

    try {
      await dispatch(pauseEntry(activeEntry._id)).unwrap();
    } finally {
      isPausingRef.current = false;
    }
  };

  // ✅ STOP
  const handleStop = async () => {
    if (!activeEntry || isStoppingRef.current) return;

    isStoppingRef.current = true;

    try {
      await dispatch(stopEntry(activeEntry._id)).unwrap();
    } finally {
      isStoppingRef.current = false;
    }
  };

  return (
    <header className="topbar-header d-flex align-items-center justify-content-between px-3 py-2">
      <img src="/logo.jpeg" alt="Logo" className="logo me-3" />

      {!isLoginPage && (
        <div className="topbar-clock">
          <button
            className={`btn btn-success btn-sm ${
              startBlockedByFixedTime ? 'topbar-start-blocked' : ''
            }`}
            onClick={handleStart}
            disabled={startDisabled}
            title={
              startBlockedByFixedTime
                ? `Einstempeln nur zwischen ${todaySchedule?.startTime} und ${todaySchedule?.endTime} Uhr möglich`
                : undefined
            }
          >
            <i className="bi bi-play-fill"></i> Start
          </button>

          <button
            className={`btn btn-warning btn-sm ${activeEntry?.isPaused ? 'active' : ''}`}
            onClick={handlePause}
            disabled={!activeEntry}
          >
            <i className="bi bi-pause-fill"></i>{' '}
            {activeEntry?.isPaused ? 'Pause beenden' : 'Pause'}
          </button>

          <button
            className="btn btn-danger btn-sm"
            onClick={handleStop}
            disabled={!activeEntry}
          >
            <i className="bi bi-stop-fill"></i> Stopp
          </button>

          <span
            className="topbar-timer"
            style={activeEntry?.isPaused ? { color: '#c98a00' } : {}}
          >
            {formatTime(activeEntry?.isPaused ? pauseTime : elapsedTime)}
          </span>
        </div>
      )}

      {!isLoginPage && (
        <div className="d-flex align-items-center gap-2 ms-auto">
          <div className="dropdown">
          <button
            className="btn btn-outline-light btn-sm dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
          >
            {user?.username}
          </button>

          <ul className="dropdown-menu dropdown-menu-end">
            <li>
              <button className="dropdown-item" onClick={() => navigate('/profile')}>
                Profil
              </button>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button className="dropdown-item text-danger" onClick={handleLogout}>
                Logout
              </button>
            </li>
          </ul>
          </div>
        </div>
      )}
    </header>
  );
};

export default TopBar;
